import React from 'react';
import { Link } from 'react-router-dom';
import { Target, PieChart, TrendingUp, Shield, Smartphone, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const HowTroveWorks = () => {
  const steps = [
    {
      number: 1,
      icon: Target,
      title: 'Set Your Goals',
      description: 'Define your financial objectives, whether it\'s buying a home, early retirement, or building generational wealth. Our platform helps you visualize and track progress toward multiple goals simultaneously.'
    },
    {
      number: 2,
      icon: PieChart,
      title: 'Choose Your Portfolio',
      description: 'Select from our range of professionally managed portfolios tailored to different risk profiles and time horizons. From defensive Fortress to growth-focused Velocity, we match your goals with the right investment strategy.'
    },
    {
      number: 3,
      icon: RefreshCw,
      title: 'Automated Management',
      description: 'Our AI-powered platform handles everything: portfolio rebalancing, tax optimization, and strategic allocation adjustments. You invest, we manage, you track progress—all from your phone.'
    },
    {
      number: 4,
      icon: TrendingUp,
      title: 'Watch Your Wealth Grow',
      description: 'Monitor your portfolio performance in real-time, receive insights on market trends, and make informed decisions. Monthly liquidity means you\'re never locked in—flexibility meets long-term investing.'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Institutional-Grade Investments',
      description: 'Access to private equity, venture capital, and alternative assets typically reserved for ultra-high net worth individuals and institutions.'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Platform',
      description: 'Manage your wealth from anywhere. Deposit funds, track goals, and adjust strategies—all from the palm of your hand.'
    },
    {
      icon: PieChart,
      title: 'Multi-Asset Diversification',
      description: 'Your portfolio spans public equities, private markets, real estate, fixed income, and alternatives—reducing risk while maximizing return potential.'
    },
    {
      icon: TrendingUp,
      title: 'Transparent Performance',
      description: 'No hidden fees. No surprises. Real-time visibility into your portfolio\'s performance, holdings, and costs.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F3ED', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-light mb-6 leading-tight" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            How Trove Works
          </h1>
          <p className="text-xl text-gray-600 font-light max-w-3xl mx-auto">
            Professional wealth management designed for High Earners, Not Wealthy Yet. We bridge the gap between "I make good money" and "I have actual wealth."
          </p>
        </div>
      </section>

      {/* The Process */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light mb-12 text-center" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            Four Simple Steps to Financial Freedom
          </h2>
          
          <div className="space-y-8">
            {steps.map((step) => {
              const IconComponent = step.icon;
              return (
                <Card key={step.number} className="p-8 border-0 shadow-md hover:shadow-xl transition-shadow">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: '#FF8C42',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        <IconComponent size={40} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div style={{
                        display: 'inline-block',
                        background: '#1a1a3e',
                        color: 'white',
                        padding: '0.25rem 1rem',
                        borderRadius: '1rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        marginBottom: '1rem'
                      }}>
                        Step {step.number}
                      </div>
                      <h3 className="text-2xl font-light mb-3" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                        {step.title}
                      </h3>
                      <p className="text-gray-600 font-light leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light mb-12 text-center" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            What Makes Trove Different
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="p-8 border-0 shadow-sm hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-[#1a1a3e]" size={28} />
                    </div>
                    <h3 className="text-xl font-light flex-1" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {feature.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Investment Philosophy */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F3ED' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light mb-8 text-center" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
            Our Investment Philosophy
          </h2>
          
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                We use technology to democratise access to institutional-grade investments: private equity, venture capital, real estate funds, and alternative assets that historically required millions to access.
              </p>
              <p>
                Our multi-asset approach reduces volatility while maximising return potential. Public markets for liquidity. Private markets for alpha. Real assets for inflation protection. All managed by AI-powered portfolio optimisation with human oversight.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to Start Building Wealth?
          </h2>
          <p className="text-xl text-gray-300 font-light mb-12 max-w-2xl mx-auto">
            Join thousands of Australians who have bridged the gap from high income to high net worth.
          </p>
          <Link to="/signup">
            <Button size="lg" style={{
              background: '#FF8C42',
              color: 'white',
              padding: '1.5rem 3rem',
              fontSize: '1.125rem',
              borderRadius: '9999px'
            }}>
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowTroveWorks;